<script setup>
import {useUserStore} from '@/stores/user.js';
import {computed, onUnmounted, ref, watch, provide} from "vue";
import SortIcon from "@/components/datatable/sortIcon.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {Icon} from "@iconify/vue";
import {persianDate} from "@/utils/persianDate.js";
import {Button} from "primevue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import UserPermissions from "@/views/users/Permissions.vue"
import Roles from "@/components/UserProfile/roles.vue"
import AddUser from "@/views/users/AddUser.vue";
import UserProfile from "@/components/UserProfile/index.vue";
import {useCustomerStore} from "@/stores/customer.js";

const userStore = useUserStore();
const customerStore = useCustomerStore();
const searchedUsers = ref([]);
const isShowEditUserModal = ref(false);
const isShowRolesModal = ref(false);
const isShowAddUserModal = ref(false);
const isShowUserProfileModal = ref(false);
const keyword = ref('');
const selectedUser = ref(null);
const searchTimer = ref(null);
const hasUpdateAccess = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('USER_MANAGEMENT_UPDATE'))
const isShowAllUsers = ref(true)

const displayUsers = computed(() => keyword.value.length === 0 ? [] : searchedUsers.value);

watch(keyword, (newVal) => {
  if (searchTimer.value) {
    clearTimeout(searchTimer.value);
    searchTimer.value = null;
  }

  if (newVal.length < 1) {
    searchedUsers.value = [];
    return;
  }

  searchedUsers.value = Array.from({length: 10}, () => ({isLoading: true}));

  searchTimer.value = setTimeout(async () => {
    fetchUsers(newVal);
    searchTimer.value = null;
  }, 1000);
});

watch(isShowAllUsers, () => {
  if (keyword.value.length < 1) return;

  if (searchTimer.value) {
    clearTimeout(searchTimer.value);
    searchTimer.value = null;
  }

  fetchUsers(keyword.value);
});

async function fetchUsers(keyword) {
  searchedUsers.value = Array.from({length: 10}, () => ({isLoading: true}));

  if (isShowAllUsers.value) {
    const results = await userStore.action_searchAccounts({keyword});
    searchedUsers.value = results;
  } else {
    const results = await customerStore.action_searchCustomers({keyword});
    searchedUsers.value = results;
  }
}

onUnmounted(() => {
  if (searchTimer.value) {
    clearTimeout(searchTimer.value);
  }
});

function showEditUserModal(row) {
  const user = row?.data || row;
  if (user.isLoading) return;
  if (hasUpdateAccess) {
    isShowEditUserModal.value = true;
    selectedUser.value = user;
    userStore.setSelectedUserData(user);
  }
}

function showRolesModal(row) {
  const user = row?.data || row;
  if (user.isLoading) return;
  if (hasUpdateAccess) {
    isShowRolesModal.value = true;
    selectedUser.value = user;
    userStore.setSelectedUserData(user);
  }
}

function showUserProfileModal(row) {
  const user = row?.data || row;
  if (user.isLoading) return;
  userStore.setSelectedUser(user);
  isShowUserProfileModal.value = true
}

function closeEditUserModal() {
  isShowEditUserModal.value = false;
  selectedUser.value = null;
}

function closeRolesModal() {
  isShowRolesModal.value = false;
  selectedUser.value = null;
}

function showAddUserModal() {
  isShowAddUserModal.value = true
}

function closeAddUserModal() {
  isShowAddUserModal.value = false
}

provide('isShowUserProfileModal', isShowUserProfileModal)
provide('isShowUserPermissions', isShowEditUserModal)
provide('isShowAddUserModal', isShowAddUserModal)
provide('isShowRolesModal', isShowRolesModal)
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-col md:flex-row justify-between gap-4 w-full">
        <Breadcrumbs/>
        <div class="flex justify-between gap-3">
          <div
              class="w-max border border-slate-200 dark:border-slate-700 dark:bg-slate-900 flex items-center rounded-md text-xs h-[32px]">
            <span @click="isShowAllUsers = false"
                  class="flex-1 font-normal px-3 py-1 rounded-r ltr:rounded-l ltr:rounded-r-none transition-all duration-150 cursor-pointer h-full items-center flex"
                  :class="
                      !isShowAllUsers
                        ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
                        : 'dark:text-slate-300'
                    ">
              Customers
            </span>

            <span @click="isShowAllUsers = true"
                  class="flex-1 font-normal px-3 py-1 rounded-l ltr:rounded-r ltr:rounded-l-none transition-all duration-150 cursor-pointer h-full items-center flex"
                  :class="
                  isShowAllUsers
                    ? 'bg-slate-900 text-white dark:bg-slate-700 dark:text-slate-300'
                    : 'dark:text-slate-300'
                ">
             All
            </span>
          </div>
          <AllButtons v-model="keyword" :showFilter="false" :showCategoriesButtons="false" :copyButton="false"
                      :buttonForNewItem="true" titleForNewItem="Add User" @buttonForNewItemClicked="showAddUserModal"/>
        </div>
      </div>
      <DataTable
          :nullSortOrder="-1"
          :value="displayUsers"
          scrollable
          scrollHeight="flex"
          :virtualScrollerOptions="{ lazy: true, itemSize: 30, delay: 200, numToleratedItems: 10 }"
          resizableColumns
          removableSort
          stripedRows
          class="text-xs w-full"
          size="small"
          tableStyle="width: 100%;"
          row-hover
          @row-click="showUserProfileModal"
          :rowClass="(data) => data.isLoading
            ? 'animate-pulse even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300 cursor-default'
            : 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300 cursor-pointer'"
      >
        <Column field="id" header="ID" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 5%; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="name" header="Name" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 50%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ slotProps.data.lastUpdate ? persianDate(slotProps.data.lastUpdate) : 'ــــ' }}
            </span>
          </template>
        </Column>
        <Column :exportable="false"
                style="width: 5%; min-width: 70px; height: 30px" v-if="hasUpdateAccess">
          <template #body="slotProps">
            <div class="flex flex-row gap-2 justify-end items-center w-full">
              <button @click.stop="showEditUserModal(slotProps.data)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                     xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M9.75684 1.98286C9.76631 2.39697 9.43829 2.74034 9.02418 2.74981C6.20248 2.81432 4.87348 3.09895 3.99627 3.97617C3.42588 4.54656 3.09862 5.31824 2.92655 6.59801C2.75159 7.89936 2.75 9.60936 2.75 11.99C2.75 14.3705 2.75159 16.0805 2.92655 17.3819C3.09862 18.6617 3.42588 19.4333 3.99627 20.0037C4.56666 20.5741 5.33834 20.9014 6.61811 21.0735C7.91946 21.2484 9.62946 21.25 12.0101 21.25C14.3906 21.25 16.1006 21.2484 17.402 21.0735C18.6818 20.9014 19.4534 20.5741 20.0238 20.0037C20.9011 19.1265 21.1857 17.7975 21.2502 14.9758C21.2597 14.5617 21.603 14.2337 22.0171 14.2432C22.4312 14.2526 22.7593 14.596 22.7498 15.0101C22.6866 17.7765 22.4374 19.7115 21.0845 21.0644C20.1889 21.9599 19.048 22.3657 17.6019 22.5601C16.189 22.75 14.3782 22.75 12.0674 22.75H11.9527C9.64191 22.75 7.8311 22.75 6.41824 22.5601C4.97214 22.3657 3.83115 21.9599 2.93561 21.0644C2.04006 20.1689 1.63435 19.0279 1.43993 17.5818C1.24998 16.1689 1.24999 14.3581 1.25 12.0473V11.9326C1.24999 9.62181 1.24998 7.811 1.43993 6.39814C1.63435 4.95204 2.04006 3.81105 2.93561 2.91551C4.2885 1.56262 6.22347 1.31345 8.98989 1.2502C9.404 1.24073 9.74737 1.56875 9.75684 1.98286ZM17.2606 2.75C15.0544 2.75 13.2711 4.53036 13.2711 6.72008C13.2711 7.21172 13.3839 7.64295 13.4996 7.95426C13.557 8.10864 13.6137 8.22949 13.6541 8.30858C13.6742 8.348 13.6901 8.37665 13.6997 8.39349L13.7091 8.40964C13.8944 8.70485 13.8506 9.08899 13.6037 9.33493L7.87005 15.0451C7.85527 15.0598 7.82684 15.0936 7.80049 15.1408C7.77466 15.1871 7.75916 15.2314 7.75308 15.2686C7.74754 15.3025 7.74952 15.3303 7.75842 15.358C7.76713 15.3852 7.79159 15.4409 7.87005 15.519L8.50421 16.1506C8.52318 16.1647 8.55267 16.184 8.59 16.2022C8.63879 16.2261 8.68606 16.2411 8.72624 16.2471C8.76432 16.2527 8.78981 16.2495 8.80787 16.2441C8.82312 16.2395 8.85657 16.2266 8.9057 16.1777L9.67724 15.4093C9.96988 15.1178 10.4431 15.1178 10.7357 15.4093C10.8507 15.5237 10.9465 15.5714 11.0148 15.5922C11.0838 15.6133 11.1522 15.6155 11.2227 15.602C11.379 15.5721 11.5211 15.4687 11.5865 15.386C11.677 15.2553 11.6741 15.1418 11.6308 15.0124C11.6067 14.9407 11.5721 14.876 11.5419 14.8284C11.5274 14.8055 11.5155 14.7892 11.5091 14.7808C11.5074 14.7785 11.5054 14.7759 11.5054 14.7759C11.2557 14.478 11.2748 14.0378 11.551 13.7627L11.7714 13.5432C12.0641 13.2518 12.5373 13.2518 12.8299 13.5432C13.2139 13.9256 13.4684 13.9208 13.591 13.8976C13.7642 13.8647 13.9255 13.7402 14.011 13.6301C14.1236 13.4689 14.1241 13.3249 14.0925 13.1925C14.0751 13.1199 14.0481 13.0565 14.0253 13.0119C14.0142 12.9903 14.0053 12.9753 14.0009 12.9683L13.9999 12.9668L13.9714 12.927L13.945 12.8744C13.9355 12.8554 13.9203 12.8325 13.8718 12.779C13.8702 12.7772 13.8672 12.7741 13.863 12.7697C13.826 12.7307 13.6958 12.5938 13.6159 12.4504C13.4791 12.2052 13.4412 11.9068 13.5599 11.602C13.6513 11.3672 13.82 11.1737 13.9758 11.0185L14.6371 10.3599C14.9112 10.087 15.3478 10.0672 15.6454 10.3142C15.765 10.4135 16.0204 10.5205 16.3848 10.596C16.7271 10.6668 17.067 10.6902 17.2606 10.6902C19.4668 10.6902 21.25 8.9098 21.25 6.72008C21.25 4.53036 19.4668 2.75 17.2606 2.75ZM13.1486 15.3763C13.1174 15.6615 13.0159 15.9669 12.7999 16.268L12.7948 16.2751L12.7895 16.2821C12.5219 16.6375 12.061 16.9687 11.5049 17.0752C11.1118 17.1505 10.6919 17.1088 10.2913 16.9147L9.96419 17.2405C9.51185 17.691 8.97167 17.7996 8.50691 17.7309C8.07947 17.6678 7.727 17.4603 7.51517 17.2795C7.50058 17.267 7.48647 17.254 7.47288 17.2405L6.81156 16.5819C6.32963 16.1019 6.18913 15.5381 6.27272 15.0266C6.34864 14.5622 6.598 14.195 6.81156 13.9823L12.1628 8.65293C12.14 8.59793 12.1168 8.53926 12.0937 8.47711C11.9339 8.04748 11.7711 7.4359 11.7711 6.72008C11.7711 3.69615 14.2317 1.25 17.2606 1.25C20.2894 1.25 22.75 3.69615 22.75 6.72008C22.75 9.74401 20.2894 12.1902 17.2606 12.1902C16.9765 12.1902 16.5339 12.1586 16.0807 12.0648C15.84 12.015 15.5614 11.9408 15.2909 11.8258L15.1456 11.9705C15.1844 12.0246 15.224 12.0862 15.2615 12.1554C15.2657 12.1619 15.2702 12.1689 15.2748 12.1764C15.2984 12.2144 15.3283 12.2655 15.3603 12.328C15.4235 12.4514 15.4998 12.6282 15.5514 12.8437C15.657 13.2855 15.6581 13.9075 15.2247 14.5117L15.2196 14.5188L15.2144 14.5258C14.9678 14.8532 14.5039 15.2511 13.8705 15.3713C13.6342 15.4161 13.392 15.4191 13.1486 15.3763ZM17.2606 5.82354C16.7588 5.82354 16.3573 6.22783 16.3573 6.72008C16.3573 7.21233 16.7588 7.61662 17.2606 7.61662C17.7623 7.61662 18.1639 7.21233 18.1639 6.72008C18.1639 6.22783 17.7623 5.82354 17.2606 5.82354ZM14.8573 6.72008C14.8573 5.39362 15.9362 4.32354 17.2606 4.32354C18.585 4.32354 19.6639 5.39362 19.6639 6.72008C19.6639 8.04654 18.585 9.11662 17.2606 9.11662C15.9362 9.11662 14.8573 8.04654 14.8573 6.72008Z"
                        fill="currentColor"/>
                </svg>
              </button>
              <button @click.stop="showUserProfileModal(slotProps.data)">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-info-icon lucide-info">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4"/>
                  <path d="M12 8h.01"/>
                </svg>
              </button>
            </div>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">
            {{ `Search for the ${isShowAllUsers ? `user's` : `customer's`} name to find them.` }}
          </h1>
        </template>
      </DataTable>
    </div>
    <UserProfile v-if="isShowUserProfileModal" manageDiscordInfo managePermissions manageKYC managePayment
                 :manageVerification="false" :userID="userStore.selectedUser.id"
                 @showUserPermissions="showEditUserModal" @showUserRoles="showRolesModal"/>
    <UserPermissions v-if="isShowEditUserModal" @close-modal="closeEditUserModal" :_selectedUser="selectedUser"/>
    <Roles v-if="isShowRolesModal" @close-modal="closeRolesModal" :_selectedUser="selectedUser"/>
    <AddUser v-if="isShowAddUserModal"/>
  </div>
</template>